import { Component, ElementRef, Input, OnDestroy, ViewChild, inject, PLATFORM_ID } from '@angular/core';
import { DictionaryService, SharedUiLibraryModule } from '@cudirect/shared-ui-library';
import { LenderService } from '@shared/service/lender/lender.service';
import { DecisionStatus, LoanType } from '@shared/enum';
import { LenderConfiguration } from '@shared/model/lender-config.model';
import { AppSummary } from 'src/postapp/models/application.model';
import { ConsumerLoanService } from 'src/postapp/services/consumer-loan.service';
import { PostappService } from 'src/postapp/postapp.service';
import { NewApplicationsService } from '@shared/service/new-applications.service';
import { Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { LOAN_DETAILS_CONFIG } from '@shared/constant/loan-details-const';
import { UserSessionService } from '@shared/service/user-session.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { IolaFeatures } from '@shared/interfaces/appInitConfig';
import { TaskCustomStipulation } from 'src/postapp/pages/tasks/tasks.model';
import { CommonModule , isPlatformBrowser } from '@angular/common';
import { ReplacePipe } from '../../pipe/replace.pipe';
import { ProductImageComponent } from '../product-image/product-image.component';

@Component({
  selector: 'dx-overview-banner',
  templateUrl: './overview-banner.component.html',
  styleUrls: ['./overview-banner.component.scss'],
  standalone: true,
  imports: [CommonModule, ReplacePipe, SharedUiLibraryModule, ProductImageComponent]
})
export class OverviewBannerComponent implements OnDestroy {
  // Inject dependencies using the new inject pattern
  private readonly dictionaryService = inject(DictionaryService);
  private readonly consumerLoanService = inject(ConsumerLoanService);
  private readonly lenderService = inject(LenderService);
  private readonly adminPortalFeaturesService = inject(AdminPortalFeaturesService);
  private readonly userSession = inject(UserSessionService);
  public readonly postapp = inject(PostappService);
  private readonly applicationService = inject(NewApplicationsService);

  public applicationSummary: AppSummary;
  isLoading = true;
  @ViewChild('rate') rate: ElementRef;
  @ViewChild('amount') amount: ElementRef;
  @ViewChild('requestedamount') requestedamount: ElementRef;
  @ViewChild('application') application: ElementRef;
  @ViewChild('payment') payment: ElementRef;
  @Input() minimizedBanner = false;
  public showConditions = false;
  public conditionsExceedsInline = false;
  public applicationStatusStyle: string;
  public readonly inprogress = DecisionStatus.StatusInProgress;
  public applicationStatusDescription: string;
  public lenderData: LenderConfiguration;
  public olaSettings: IolaFeatures;
  public configuredStatusText: string;
  public configuredStatusStyle: string;
  public loanType: string;
  public loanDetailFields = [];
  // OAO or Consumer product?
  public applicationType: string;
  // Membership name, auto, personal, etc
  public applicationTypeName: string;
  // Customized Display Name for Membership name, auto, personal, etc
  public applicationTypeDisplayName: string;

  public showLoanDetails = false;
  // flag to display  monthly payment
  public showMonthlyPayment: boolean;
  // flag to check if the loan status is Funded
  public isFundedLoan: boolean;
  // Stores Label for displaying amount in overview banner
  public overviewAmountLabel: string;
  // flag to display Rate
  public showRate: boolean;
  // flag to display Term
  public showTerm: boolean;
  private readonly subscription: Subscription[] = [];
  // Applicant Name
  public readonly applicantName: string;
  public readonly applicantFirstName: string;
  public ignoreLoanAmountSettings: boolean;
  // postapp conditions
  public conditions: TaskCustomStipulation[] = [];
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  constructor() {
    this.applicantName = this.userSession.userFullName();
    this.applicantFirstName = this.userSession.userFirstName();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const appSub = this.applicationService.currentApplication.pipe(first()).subscribe((app) => {});
    const condSub = this.postapp?.conditions$?.subscribe?.((tasks) => {
      this.conditions = tasks || [];
      this.conditionsExceedsInline = this.conditions.length > 1 || this.conditions[0]?.name.length > 115;
    });
    // We need to remove this whole sub.
    const postAppSub = this.postapp.applicationSummary$.subscribe((applicationSummary) => {
      if (!Object.keys(applicationSummary || {}).length) {
        return;
      }

      if (applicationSummary.appStatus === 'ServerError') {
        return;
      }
      this.applicationSummary = { ...applicationSummary };
      this.lenderData = this.lenderService.lenderDataInfo;
      this.olaSettings = this.adminPortalFeaturesService?.config?.olaFeatures;
      this.applicationType = this.postapp.applicationType;
      setTimeout(() => {
        this.loanDetailFields = this.getLoanDetailsFields(
          applicationSummary?.loanType,
          applicationSummary?.loanStatus,
          applicationSummary?.loanStage
        );
      });

      const membershipTypeName = this.isYouthMembership() ? 'Youth Membership' : `${this.getMembershipTypeName()} Membership`;

      this.setApplicationTypeName(membershipTypeName);

      const loanPurpose = this.applicationSummary.loanPurpose ? this.applicationSummary.loanPurpose : '';
      const autoTypeDisplayName =
        this.applicationSummary?.collateralDetails?.length > 0
          ? this.applicationSummary.collateralDetails[0].condition + ' ' + this.applicationSummary.loanType + ' ' + loanPurpose
          : this.applicationSummary.loanType;

      this.applicationTypeDisplayName =
        (this.applicationSummary.sourceLoanType || this.applicationSummary.loanType) === LoanType.Auto
          ? autoTypeDisplayName
          : this.applicationTypeName;

      this.setApplicationStatusStyle();
      this.configuredLoanStatus();
      this.getLoanAmountSettings();

      this.showMonthlyPayment = this.canShowPayment();
      this.showTerm = this.canShowTerm();
      this.showRate = this.canShowRate();
      this.isFundedLoan = this.fundedLoan();
      this.overviewAmountLabel = this.amountLabel();
      this.isLoading = false;
    });

    this.subscription.push(appSub);
    this.subscription.push(postAppSub);
    this.subscription.push(condSub);
  }

  public setApplicationTypeName(membershipTypeName: string): void {
    if (this.postapp.applicationType.toLowerCase() === 'oao') {
      this.applicationTypeName = membershipTypeName;
      this.loanType = membershipTypeName;
    } else if (this.applicationSummary.sourceLoanType === LoanType.UnsecuredLoan) {
      this.applicationTypeName = this.dictionaryService.read('overviewBanner/personalLoanLabel');
    } else {
      this.applicationTypeName = this.applicationSummary.sourceLoanType;
      this.loanType = this.applicationSummary.loanType ?? this.applicationSummary.sourceLoanType;
    }
  }

  private getMembershipTypeName(): string {
    if (!this.isBrowser) {
      return this.applicationSummary?.membershipTypeName ?? '';
    }

    return this.applicationSummary?.membershipTypeName ?? JSON.parse(sessionStorage.getItem('membershipTypeName') || 'null') ?? '';
  }

  private isYouthMembership(): boolean {
    if (!this.isBrowser) {
      return this.applicationSummary?.isYouthMembership ?? false;
    }

    return this.applicationSummary?.isYouthMembership ?? JSON.parse(sessionStorage.getItem('isYouthMembership') || 'false') ?? false;
  }

  // Note: This is getting called in PostApp even for lenders that do not have loans configured.
  // We should fix this to only call this for lenders that have loans configured and when
  // the user is viewing a loan in post app
  /**
   * Function to get loan amount settings
   * @returns void
   */
  private getLoanAmountSettings(): void {
    const selectedLoanGroup = this.adminPortalFeaturesService?.config?.olaConfiguration?.consumerLoanGroups.filter(
      (t) => t.loanGroup?.toLowerCase() === this.applicationSummary?.selectedLoanGroup?.toLowerCase()
    )[0];
    if (selectedLoanGroup) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const selectedLoanProduct: any = selectedLoanGroup?.loanProducts?.filter(
        (t) =>
          !selectedLoanGroup.isLoanGroupHasProduct ||
          t.productName.toLowerCase() === this.applicationSummary?.selectedLoanProduct?.toLowerCase()
      )[0];
      if (selectedLoanProduct) {
        this.ignoreLoanAmountSettings = selectedLoanProduct.ignoreLoanAmountSettings;
      }
    }
  }

  public canShowPayment(): boolean {
    return (
      this.olaSettings?.canShowEstPayment &&
      (Number(this.applicationSummary?.decision?.estMonthlyPayment) > 0 ||
        (this.isFundedLoan && Number(this.applicationSummary?.monthlyFundedPayment) > 0))
    );
  }

  public fullPropertyAddress() {
    if (this.applicationSummary?.subjectProperty) {
      const street = this.applicationSummary.subjectProperty.propertyAddress;
      const city = this.applicationSummary.subjectProperty.city;
      const state = this.applicationSummary.subjectProperty.stateAbbreviation;
      const zip = this.applicationSummary.subjectProperty.zip;
      let fullAddress = '';
      if (street) {
        fullAddress += street + ', ';
      }
      if (city) {
        fullAddress += city + ', ';
      }
      if (state) {
        fullAddress += state + ', ';
      }
      if (zip) {
        fullAddress += zip;
      }
      // trim last space and comma
      return fullAddress.replace(/,(\s+)?$/, '');
    }
  }

  public amountLabel() {
    let foundLabel = 'fundedAmount';
    let approvedLabel = 'approvedLoanAmount';
    let requested = 'requestedLoanAmount';
    let amountLabelSuffix = requested;
    const isApproved = this.configuredStatusText?.toLowerCase() === DecisionStatus.Approved.toLowerCase();

    if (this.applicationTypeName?.toLowerCase() === LoanType.CreditCard.toLowerCase()) {
      foundLabel = 'fundedCreditAmount';
      approvedLabel = 'approvedCreditLimitAmount';
      requested = 'requestedCreditLimitAmount';
      amountLabelSuffix = requested;
    }

    amountLabelSuffix = this.isFundedLoan ? foundLabel : (isApproved && approvedLabel) || amountLabelSuffix;

    return this.dictionaryService.read(`overviewBanner/${amountLabelSuffix}`);
  }

  /**
   * Function for funded loan status
   */
  public fundedLoan() {
    if (this.applicationSummary?.loanStatus) {
      return this.consumerLoanService.isFundedLoan(this.applicationSummary.loanStatus);
    }
  }

  public helocLoanType() {
    return (
      this.applicationSummary.loanType === LoanType.HELOC ||
      this.applicationSummary.loanType === LoanType.HomeImprovement ||
      this.applicationSummary.loanType === LoanType.HomeEquityLoan2 ||
      this.applicationSummary.loanType === LoanType.ManufacturedHome
    );
  }

  public get loanCounterOffer() {
    return (
      this.applicationSummary.loanStatus.toLowerCase() === 'counter' ||
      this.applicationSummary.loanStatus.toLowerCase() === 'counter offered'
    );
  }

  public creditCardType() {
    return this.applicationSummary.loanType === LoanType.CreditCard;
  }

  /** Setting up the status text and style based on lender config */
  /** TODO: is alt style and alt status still being used? */
  /** Hollowed this out, will continue to remove this. */
  public configuredLoanStatus(): void {
    if (this.applicationSummary?.loanStatus) {
      this.configuredStatusText = this.formatStatusText(this.applicationSummary?.loanStatus);
    }
  }

  private formatStatusText(loanStatus: string) {
    switch (loanStatus) {
      case 'Decline':
      case 'Contact your Lender':
        return 'Please Contact Us';
      case 'Refer':
      case 'Pending':
        return 'Under Review';
      default:
        return loanStatus;
    }
  }

  public get canShowApplicationNumber() {
    switch (this.applicationSummary?.loanStatus?.toLowerCase()) {
      case DecisionStatus.Refer.toLowerCase():
      case DecisionStatus.InProgress.toLowerCase():
      case DecisionStatus.Pending.toLowerCase():
      case DecisionStatus.StatusInProgress.toLowerCase():
      case DecisionStatus.Decline.toLowerCase():
        return true;
      default:
        return false;
    }
  }

  public setApplicationStatusStyle() {
    switch (this.applicationSummary?.loanStatus?.toLowerCase()) {
      case DecisionStatus.Approved.toLowerCase():
        this.applicationStatusStyle = 'success-theme';
        break;
      case DecisionStatus.Refer.toLowerCase():
      case DecisionStatus.Declined.toLowerCase():
      case DecisionStatus.Counter.toLowerCase():
      case DecisionStatus.InProgress.toLowerCase():
      case DecisionStatus.Pending.toLowerCase():
      case DecisionStatus.StatusInProgress.toLowerCase():
        this.applicationStatusStyle = 'warning-theme';
        break;
      case DecisionStatus.Withdrawn.toLowerCase():
        // No style is default grey color
        this.applicationStatusStyle = '';
        break;
      default:
        this.applicationStatusStyle = '';
    }
  }

  public get isLoanApproved() {
    return this.configuredStatusText?.toLowerCase() === DecisionStatus.Approve.toLowerCase();
  }

  public canShowTerm(): boolean {
    return (
      this.olaSettings.canShowTerm &&
      (Number(this.applicationSummary?.decision?.term) > 0 || (this.isFundedLoan && Number(this.applicationSummary?.fundedTerm) > 0))
    );
  }
  public canShowRate(): boolean {
    return (
      this.olaSettings.canShowRate &&
      (Number(this.applicationSummary?.decision?.rate) > 0 || (this.isFundedLoan && Number(this.applicationSummary?.fundedRate) > 0))
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getLoanDetailsFields(loanType: string, status: string, stage: string): any[] {
    let loan = LOAN_DETAILS_CONFIG.default;
    let config = loan.default;
    if (loanType && status) {
      if (LOAN_DETAILS_CONFIG[loanType?.toLowerCase()]) {
        loan = LOAN_DETAILS_CONFIG[loanType.toLowerCase()];
      }
      stage = stage?.toLowerCase();
      if (stage === 'funded') {
        config = loan.default;
      } else if ((stage === 'withdraw' || stage === 'withdrawn') && loan.withdraw) {
        config = loan.withdraw;
      } else if (loan[status?.toLowerCase()]) {
        config = loan[status?.toLowerCase()];
      } else {
        config = loan.default;
      }
    }
    if (this.applicationSummary?.hideLoanRate) {
      config = config.filter((obj) => obj.template !== 'rate');
    }

    if (this.applicationSummary?.hideEstimatedPaymentAndTerm) {
      config = config.filter((obj) => obj.template !== 'payment');
    }
    return config;
  }

  /**
   * Unsubscribe from all subscriptions
   */
  public ngOnDestroy(): void {
    this.subscription.forEach((sub) => sub.unsubscribe());
  }
}
